import { CompanyDetailSelectModel } from 'hq/services/mappers/mappers';
import {
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItemButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { LwButton, LWModal, theme } from 'redesign';
import { useGetOrts } from 'hq/hooks/queryHooks/companies/use-get-orts';
import { useParams } from 'react-router-dom';
import { invariant } from 'shared/utils/utils';
import { useTranslation } from 'react-i18next';
import { IconAdd, IconDuplicate } from 'assets/img';
import { CompanyOrt } from 'types/companies/company-types';
import { EmptyOrtList } from './empty-company-orts';
import { CompanyOrtForm } from '../../../../forms/company-ort-form';
import { DAY_ORDER, ENGLISH_TO_DUTCH, HOLIDAY_OPTIONS } from '../../../../forms/utils';
import { DayHeaderCell } from '../../../../forms/ort-table-day-header-cell';
import { OrtTableRow } from '../../../../forms/ort-table-row';

type OrtType = NonNullable<ReturnType<typeof useGetOrts>['data']>[0];

export const CompanyOrts = ({ data }: { data: CompanyDetailSelectModel }) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const [showModal, setShowModal] = useState(false);
  const [editingOrt, setEditingOrt] = useState<OrtType | undefined>(undefined);
  const [selected, setSelected] = useState<OrtType | null>(null);

  const { data: ortsData } = useGetOrts(data.id);
  const { id: companyId } = useParams<'id'>();
  invariant(companyId, 'Company ID not provided');

  useEffect(() => {
    let orts = ortsData;
    if (orts && !selected) {
      orts = orts.map((ort) => ({
        ...ort,
        calendar: {
          ...ort.calendar,
          workDays: Object.fromEntries(
            Object.entries(ort.calendar?.workDays ?? {}).sort(([dayA], [dayB]) => {
              return DAY_ORDER.indexOf(dayA) - DAY_ORDER.indexOf(dayB);
            })
          ),
        },
      }));

      setSelected(orts[0]);
    }
  }, [ortsData, selected, data.id]);

  const handleOpenModal = (ort?: OrtType) => {
    setEditingOrt(ort);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrt(undefined);
  };

  const sortOrtsByStartDateDescending = (a: CompanyOrt, b: CompanyOrt) => {
    return new Date(b.utcStartDate).getTime() - new Date(a.utcStartDate).getTime();
  };

  const getActiveOrtId = (employmentType: string) => {
    const now = new Date();
    const ortsForType = orts
      .filter((ort) => ort.employmentType === employmentType)
      .sort(sortOrtsByStartDateDescending);

    const activeOrt = ortsForType.find((ort) => new Date(ort.utcStartDate) <= now);
    return activeOrt?.id;
  };

  const orts = (ortsData ?? []).map((ort) => ({
    ...ort,
    calendar: {
      ...ort.calendar,
      workDays: Object.fromEntries(
        Object.entries(ort.calendar?.workDays ?? {}).sort(([dayA], [dayB]) => {
          return DAY_ORDER.indexOf(dayA) - DAY_ORDER.indexOf(dayB);
        })
      ),
    },
  }));

  if (orts.length === 0) {
    return (
      <EmptyOrtList
        showModal={showModal}
        handleOpenModal={() => handleOpenModal(undefined)}
        handleCloseModal={handleCloseModal}
        editingOrt={editingOrt}
        companyId={companyId}
        t={t}
      />
    );
  }

  if (!selected) {
    return null;
  }

  const activeFreelancerOrtId = getActiveOrtId('FREELANCER');
  const activeTempworkerOrtId = getActiveOrtId('TEMPWORKER');

  const allHolidays = HOLIDAY_OPTIONS.map((holidayName) => ({
    name: holidayName,
    ranges: selected.holidays?.[holidayName] ?? [],
  }));

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <OrtListContainer>
          <StyledOrtList>
            <StyledOrtListBox>
              {orts.map((ort) => {
                const isActive =
                  ort.id === activeFreelancerOrtId || ort.id === activeTempworkerOrtId;

                return (
                  <ListItemButton
                    key={ort.id}
                    selected={selected.id === ort.id}
                    onClick={() => setSelected(ort)}
                  >
                    <ActivePillContainer>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1">
                          {`${ort.name ?? 'Unnamed ORT'} - ${ort.employmentType}`}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                          Start date: {new Date(ort.utcStartDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      {isActive && (
                        <ActivePill>
                          <Typography variant="caption">
                            {t('company_ort_table.active', {
                              ns: 'hq-screen-specific',
                            })}
                          </Typography>
                        </ActivePill>
                      )}
                    </ActivePillContainer>
                  </ListItemButton>
                );
              })}
            </StyledOrtListBox>

            <ActionButtons>
              <LwButton
                color="primary"
                fullWidth
                onClick={() => handleOpenModal(undefined)}
                startIcon={<IconAdd />}
                iconColorMode="fill"
              >
                {t('buttons.add', { ns: 'web-common' })}
              </LwButton>
              <LwButton
                color="primary"
                fullWidth
                onClick={() => handleOpenModal(selected)}
                startIcon={<IconDuplicate />}
                iconColorMode="fill"
              >
                {t('buttons.duplicate', { ns: 'web-common' })}
              </LwButton>
            </ActionButtons>
          </StyledOrtList>

          <OrtTableCard>
            <CardContent>
              <OrtDetailsBox>
                <Typography variant="h5" gutterBottom>
                  {selected.name ?? 'Unnamed ORT'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.employmentType}
                </Typography>
              </OrtDetailsBox>

              <TableContainer>
                <StyledTable>
                  <StyledColgroup />
                  <StyledTableHead>
                    <TableRow>
                      <DayHeaderCell
                        rowSpan={1}
                        label={t('company_ort_table.hearder.workdays', {
                          ns: 'hq-screen-specific',
                        })}
                      />
                      <StyledTableCell>
                        {t('company_ort_table.hearder.start_time', { ns: 'hq-screen-specific' })}
                      </StyledTableCell>
                      <StyledTableCell>
                        {t('company_ort_table.hearder.end_time', { ns: 'hq-screen-specific' })}
                      </StyledTableCell>
                      <StyledTableCell>ORT</StyledTableCell>
                    </TableRow>
                  </StyledTableHead>

                  <TableBody>
                    {Object.entries(selected.calendar?.workDays ?? {}).map(([day, ranges]) => {
                      const translatedDayName = ENGLISH_TO_DUTCH[day] ?? day;
                      return <OrtTableRow key={day} label={translatedDayName} ranges={ranges} />;
                    })}

                    <TableRow>
                      <TableDividerCell colSpan={4}>
                        {t('company_ort_table.hearder.holidays', { ns: 'hq-screen-specific' })}
                      </TableDividerCell>
                    </TableRow>

                    {allHolidays.map(({ name: holidayName, ranges }) => (
                      <OrtTableRow key={holidayName} label={holidayName} ranges={ranges} />
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
            </CardContent>
          </OrtTableCard>
        </OrtListContainer>
      </Grid>

      <LWModal isOpen={showModal} onClose={handleCloseModal} testId="ort-modal">
        {showModal && (
          <CompanyOrtForm onClose={handleCloseModal} ort={editingOrt} companyId={companyId} />
        )}
      </LWModal>
    </Grid>
  );
};

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.lwBlack[10],
  mt: 2,
}));

const StyledTable = styled(Table)({
  '& .MuiTableCell-root': {
    fontSize: 14,
    color: 'black',
  },
});

const StyledTableCell = styled(TableCell)({
  fontWeight: 'bold',
  width: '20%',
});

const OrtListContainer = styled(Box)(({ theme }) => ({
  height: '70vh',
  display: 'flex',
  gap: theme.spacing(2),
}));

const StyledOrtList = styled(List)(({ theme }) => ({
  width: 400,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const StyledOrtListBox = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

const OrtDetailsBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ActionButtons = styled(Box)({
  padding: '8px',
  display: 'flex',
  gap: '8px',
});

const OrtTableCard = styled(Card)({
  flex: 1,
  overflow: 'auto',
});

const TableDividerCell = styled(TableCell)({
  fontWeight: 'bold',
  backgroundColor: theme.palette.lwBlack[10],
  borderBottom: `2px solid ${theme.palette.divider}`,
  mt: 2,
});

const BaseCol = () => <col />;
const DividerCol = () => <col style={{ borderLeft: `1px solid ${theme.palette.divider}` }} />;
const StyledColgroup = () => (
  <colgroup>
    <BaseCol />
    <DividerCol />
    <BaseCol />
    <BaseCol />
    <BaseCol />
  </colgroup>
);

const ActivePill = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.lwPrimaryAccentLight[40],
  color: theme.palette.lwWhite[100],
  padding: '2px 8px',
  borderRadius: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}));

const ActivePillContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flex: 1,
});
